import { PAYMENT_FREQUENCIES } from 'constants/digitalLoan';
import {
  MANUAL_CATALOG_LEVELS,
  MANUAL_CONDITION_ADJUSTMENT,
  MANUAL_MODEL_CODE,
  MANUAL_NAME_MAX_LENGTH,
  OTHER_OPTION_ID,
  PHASE2_MOCK_SCENARIOS,
  PRODUCT_CONDITIONS,
} from 'constants/digitalLoanPhase2';
import { buildMockPreOffer } from './mocks';

const CONSERVATION_STATE_MAP = Object.freeze({
  [PRODUCT_CONDITIONS.EXCELLENT]: 'EXCELENTE',
  [PRODUCT_CONDITIONS.GOOD]: 'BUENO',
  [PRODUCT_CONDITIONS.FAIR]: 'REGULAR',
});

const ANGLE_ORDER = Object.freeze([
  'FRONTAL',
  'TRASERA',
  'LATERAL_DERECHA',
  'LATERAL_IZQUIERDA',
  'SUPERIOR',
  'INFERIOR',
  'ETIQUETA',
  'OTRO',
]);

const REQUIRED_ANGLE_ENUMS = Object.freeze([
  'TRASERA',
  'LATERAL_DERECHA',
  'LATERAL_IZQUIERDA',
  'SUPERIOR',
  'INFERIOR',
]);

const PAYMENT_FREQUENCY_LABEL = Object.freeze({
  [PAYMENT_FREQUENCIES.MONTHLY]: 'Mensual',
  [PAYMENT_FREQUENCIES.BIWEEKLY]: 'Catorcenal',
});

function asArray(payload) {
  if (Array.isArray(payload)) return payload;
  if (!payload) return [];
  return [payload];
}

export function normalizeFamily(dto = {}) {
  return {
    id: String(dto.ClaveFamilia ?? ''),
    name: dto.Familia || '',
    raw: dto,
  };
}

export function normalizeSubFamily(dto = {}) {
  return {
    id: String(dto.IdFamiliaSubFamilia ?? ''),
    name: dto.SubFamilia || '',
    claveFamilia: dto.ClaveFamilia || '',
    claveSubFamilia: dto.ClaveSubFamilia || '',
    raw: dto,
  };
}

export function normalizeArticle(dto = {}) {
  return {
    id: String(dto.IdTipoArticulo ?? ''),
    name: dto.TipoArticulo || '',
    idSubFamiliaTipoArticulo: dto.IdSubFamiliaTipoArticulo,
    idFamiliaSubFamilia: dto.IdFamiliaSubFamilia,
    raw: dto,
  };
}

export function normalizeBrand(dto = {}) {
  return {
    id: String(dto.IdMarca ?? ''),
    name: dto.Marca || '',
    idTipoArticulo: dto.IdTipoArticulo,
    idSubFamiliaTipoArticulo: dto.IdSubFamiliaTipoArticulo,
    idFamiliaSubFamilia: dto.IdFamiliaSubFamilia,
    raw: dto,
  };
}

/**
 * Label del select: MODELO - MODELO_COMERCIAL.
 * COM/TEL también concatenan ALMACENAMIENTO y MEMORIA_RAM.
 */
export function isComOrTelFamily(familyCode) {
  const code = String(familyCode || '').toUpperCase();
  return code.startsWith('COM') || code.startsWith('TEL');
}

export function parseHomologado(value) {
  return value === true || value === 'true' || value === 1 || value === '1';
}

export function parseValorPromedio(value) {
  if (value === undefined || value === null || value === '') return null;
  const num = Number(value);
  return Number.isFinite(num) && num > 0 ? num : null;
}

export function estimatedValueStateFromModel(model) {
  const valorPromedio = parseValorPromedio(model?.valorPromedio);
  if (valorPromedio == null) {
    return { estimatedValue: '', estimatedValueLocked: false };
  }
  return {
    estimatedValue: valorPromedio,
    estimatedValueLocked: model?.homologado === true,
  };
}

function joinLabelParts(parts) {
  return parts
    .map((part) => String(part || '').trim())
    .filter(Boolean)
    .join(' - ');
}

export function buildModelOptionLabel(dto = {}, { familyCode } = {}) {
  const parts = [dto.MODELO, dto.MODELO_COMERCIAL];
  if (isComOrTelFamily(familyCode)) {
    parts.push(dto.ALMACENAMIENTO, dto.MEMORIA_RAM);
  }
  return joinLabelParts(parts);
}

export function normalizeModel(dto = {}, index = 0, options = {}) {
  // name: prioridad histórica para payload / displayName (no el label del select).
  const name = dto.MODELO_COMERCIAL || dto.MODELO || dto.DESCRIPCION || '';
  // ID_MODELO suele ser 0 en todos los renglones; no sirve como value del select.
  const keyBase = dto.MODELO || dto.DESCRIPCION || dto.MODELO_COMERCIAL || 'modelo';
  const id = `${keyBase}-${index}`;
  const label = buildModelOptionLabel(dto, options) || name || String(keyBase);
  const description = String(dto.DESCRIPCION || '').trim();
  return {
    id: String(id),
    name: name || String(keyBase),
    label,
    description,
    sapModelCode: String(dto.ID_MODELO ?? 0),
    homologado: parseHomologado(dto.HOMOLOGADO),
    valorPromedio: parseValorPromedio(dto.VALOR_PROMEDIO),
    raw: dto,
  };
}

export function normalizeCatalogList(payload, normalizeItem) {
  return asArray(payload)
    .map((item, index) => normalizeItem(item, index))
    .filter((item) => item.id && item.name);
}

export function mapConservationState(productCondition) {
  return CONSERVATION_STATE_MAP[productCondition] || null;
}

/**
 * Ordena fotos y ángulos para el multipart guarantee.
 * photoAngles debe alinearse 1:1 con el orden de `photos`.
 * FRONTAL + 5 ángulos required. ETIQUETA y OTRO solo si hay archivo.
 */
export function buildGuaranteePhotoPayload(photos = {}) {
  const photoFiles = [];
  const photoAngles = [];

  if (photos.front) {
    photoFiles.push(photos.front);
    photoAngles.push('FRONTAL');
  }

  (photos.angles || []).slice(0, REQUIRED_ANGLE_ENUMS.length).forEach((file, index) => {
    if (!file) return;
    photoFiles.push(file);
    photoAngles.push(REQUIRED_ANGLE_ENUMS[index] || 'OTRO');
  });

  if (photos.label) {
    photoFiles.push(photos.label);
    photoAngles.push('ETIQUETA');
  }

  if (photos.document) {
    photoFiles.push(photos.document);
    photoAngles.push('OTRO');
  }

  return { photoFiles, photoAngles };
}

function isOtherCatalogName(name = '') {
  const normalized = String(name)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();
  return /^(otro|otros|otra|otras)$/.test(normalized);
}

/**
 * Manual solo por sentinel UI. OTROS/OTRO de SAP no es manual.
 */
function isManualCatalogItem(item) {
  return !!(item && (
    item.isManual === true
    || item.id === OTHER_OPTION_ID
  ));
}

const PHOTO_MATCH_BLOCKED = Object.freeze([
  'REJECTED',
  'UNREADABLE',
  'UNAVAILABLE',
]);

/**
 * True si guarantee debe bloquear el avance a scoring.
 */
export function isPhotoMatchBlocked(outcome) {
  return PHOTO_MATCH_BLOCKED.includes(String(outcome || '').toUpperCase());
}

/**
 * Ajuste de valor declarado solo para path manual.
 * Redondea a entero (sin decimales).
 */
export function applyManualConditionAdjustment(rawValue, productCondition) {
  const raw = Number(rawValue);
  if (!Number.isFinite(raw) || raw <= 0) return raw;
  const factor = MANUAL_CONDITION_ADJUSTMENT[productCondition];
  if (factor == null) return Math.round(raw);
  return Math.round(raw * factor);
}

function isManualModelItem(model) {
  if (!model) return false;
  if (isManualCatalogItem(model)) return true;
  return model.id === MANUAL_MODEL_CODE || model.id === OTHER_OPTION_ID;
}

function sanitizeManualName(value) {
  return String(value ?? '').trim().slice(0, MANUAL_NAME_MAX_LENGTH);
}

/**
 * Nombre tipado del usuario. Ignora el label sentinel OTRO/other.
 */
function resolveTypedManualName(item, typedValue) {
  const typed = sanitizeManualName(typedValue);
  if (typed && typed !== OTHER_OPTION_ID) return typed;
  if (!item) return '';
  const name = sanitizeManualName(item.name);
  if (!name || name === OTHER_OPTION_ID || isOtherCatalogName(name)) {
    return '';
  }
  return name;
}

function hasGuaranteeValue(value) {
  return value != null && String(value).trim() !== '';
}

function assertNoUiOtherSentinel(guarantee) {
  const catalogKeys = [
    'sapFamilyCode',
    'sapFamilyName',
    'sapSubfamilyCode',
    'sapArticleCode',
    'sapArticleName',
    'sapBrandCode',
    'sapBrandName',
    'sapModelCode',
    'sapModelName',
    'manualFamilyName',
    'manualSubfamilyName',
    'manualArticleName',
    'manualBrandName',
    'manualModelName',
  ];
  const hasOther = catalogKeys.some(
    (key) => String(guarantee[key] ?? '') === OTHER_OPTION_ID,
  );
  if (hasOther) {
    const error = new Error('GuaranteeRequestDTO no debe enviar el sentinel UI other');
    error.code = 'GUARANTEE_DTO_INVALID_OTHER';
    throw error;
  }
}

function assertExclusiveCatalogPaths(guarantee) {
  const groups = [
    {
      sap: ['sapFamilyCode', 'sapFamilyName'],
      manual: ['manualFamilyName'],
    },
    {
      sap: ['sapSubfamilyCode'],
      manual: ['manualSubfamilyName'],
    },
    {
      sap: ['sapArticleCode', 'sapArticleName'],
      manual: ['manualArticleName'],
    },
    {
      sap: ['sapBrandCode', 'sapBrandName'],
      manual: ['manualBrandName'],
    },
    {
      sap: ['sapModelCode', 'sapModelName'],
      manual: ['manualModelName'],
    },
  ];

  const mixed = groups.some(({ sap, manual }) => {
    const hasSap = sap.some((key) => hasGuaranteeValue(guarantee[key]));
    const hasManual = manual.some((key) => hasGuaranteeValue(guarantee[key]));
    return hasSap && hasManual;
  });

  if (mixed) {
    const error = new Error(
      'GuaranteeRequestDTO no debe mezclar sap y manual en el mismo nivel',
    );
    error.code = 'GUARANTEE_DTO_MIXED_LEVEL';
    throw error;
  }
}

/**
 * Construye GuaranteeRequestDTO + lista de archivos.
 * Lanza si faltan campos required del Swagger.
 */
export function buildGuaranteeRequestPayload(phase2 = {}) {
  const family = phase2.selectedFamily;
  const subFamily = phase2.selectedSubFamily;
  const article = phase2.selectedArticle;
  const brand = phase2.selectedBrand;
  const model = phase2.selectedModel;
  const typed = phase2.manualCatalogValues || {};
  const startLevel = phase2.manualCatalogStartLevel;
  const conservationState = mapConservationState(phase2.productCondition);
  const includeDocument = phase2.photoConsents?.hasInvoice === true;
  const { photoFiles, photoAngles } = buildGuaranteePhotoPayload(
    includeDocument
      ? phase2.photos
      : { ...phase2.photos, document: null },
  );

  const familyManual = isManualCatalogItem(family)
    || startLevel === MANUAL_CATALOG_LEVELS.FAMILY;
  const subfamilyManual = familyManual
    || isManualCatalogItem(subFamily)
    || startLevel === MANUAL_CATALOG_LEVELS.SUBFAMILY;
  const articleManual = familyManual
    || subfamilyManual
    || isManualCatalogItem(article)
    || startLevel === MANUAL_CATALOG_LEVELS.ARTICLE;
  const brandManual = articleManual
    || isManualCatalogItem(brand)
    || startLevel === MANUAL_CATALOG_LEVELS.BRAND;
  const modelManual = brandManual
    || isManualModelItem(model)
    || startLevel === MANUAL_CATALOG_LEVELS.MODEL;
  const isManualPath = familyManual
    || articleManual
    || brandManual
    || modelManual;
  const rawDeclaredValue = Number(phase2.estimatedValue);
  const customerDeclaredValue = isManualPath
    ? applyManualConditionAdjustment(
      rawDeclaredValue,
      phase2.productCondition,
    )
    : rawDeclaredValue;

  const manualFamilyName = familyManual
    ? resolveTypedManualName(family, typed.familyName)
    : '';
  const manualSubfamilyName = subfamilyManual
    ? resolveTypedManualName(subFamily, typed.subfamilyName)
    : '';
  const manualArticleName = articleManual
    ? resolveTypedManualName(article, typed.articleName)
    : '';
  const manualBrandName = brandManual
    ? resolveTypedManualName(brand, typed.brandName)
    : '';
  const manualModelName = modelManual
    ? resolveTypedManualName(model, typed.modelName)
    : '';

  const missing = [];
  if (familyManual) {
    if (!manualFamilyName) missing.push('manualFamilyName');
  } else if (!family?.id || !family?.name) {
    missing.push('sapFamilyCode/sapFamilyName');
  }
  if (articleManual) {
    if (!manualArticleName) missing.push('manualArticleName');
  } else if (!article?.id || !article?.name) {
    missing.push('sapArticleCode/sapArticleName');
  }
  if (brandManual) {
    if (!manualBrandName) missing.push('manualBrandName');
  } else if (!brand?.id || !brand?.name) {
    missing.push('sapBrandCode/sapBrandName');
  }
  if (!conservationState) missing.push('conservationState');
  const hasDeclaredValue = (
    Number.isFinite(customerDeclaredValue)
    && customerDeclaredValue > 0
  );
  if (isManualPath && !hasDeclaredValue) {
    missing.push('customerDeclaredValue');
  }
  if (!phase2.photoConsents?.isOwner) missing.push('pledgeCommitmentAccepted');
  if (!photoAngles.length || !photoFiles.length) missing.push('photoAngles/photos');

  if (missing.length) {
    const error = new Error(
      `GuaranteeRequestDTO incompleto: faltan ${missing.join(', ')}`,
    );
    error.code = 'GUARANTEE_DTO_INCOMPLETE';
    error.missing = missing;
    throw error;
  }

  const guarantee = {
    conservationState,
    pledgeCommitmentAccepted: true,
    photoAngles,
  };
  if (hasDeclaredValue) {
    guarantee.customerDeclaredValue = customerDeclaredValue;
  }

  if (familyManual) {
    guarantee.manualFamilyName = manualFamilyName;
  } else {
    guarantee.sapFamilyCode = String(family.id);
    guarantee.sapFamilyName = family.name;
  }

  if (subfamilyManual) {
    if (manualSubfamilyName) {
      guarantee.manualSubfamilyName = manualSubfamilyName;
    }
  } else {
    const subfamilyCode = subFamily?.claveSubFamilia
      || subFamily?.raw?.ClaveSubFamilia;
    if (subfamilyCode) {
      guarantee.sapSubfamilyCode = String(subfamilyCode);
    }
  }

  if (articleManual) {
    guarantee.manualArticleName = manualArticleName;
  } else {
    guarantee.sapArticleCode = String(article.id);
    guarantee.sapArticleName = article.name;
  }

  if (brandManual) {
    guarantee.manualBrandName = manualBrandName;
  } else {
    guarantee.sapBrandCode = String(brand.id);
    guarantee.sapBrandName = brand.name;
  }

  if (modelManual) {
    if (manualModelName) {
      guarantee.manualModelName = manualModelName;
    }
  } else if (model?.id) {
    guarantee.sapModelCode = String(
      model.sapModelCode
      ?? model.raw?.ID_MODELO
      ?? 0,
    );
    guarantee.sapModelName = model.name
      || model.raw?.MODELO
      || model.raw?.DESCRIPCION
      || '';
  }

  assertNoUiOtherSentinel(guarantee);
  assertExclusiveCatalogPaths(guarantee);
  return { guarantee, photos: photoFiles };
}

/**
 * PhotoUploadedDTO: prioriza `angle`; acepta `type` local/mock legado.
 */
export function mapPhotoUploaded(photo = {}) {
  return {
    s3Key: photo.s3Key || null,
    s3Url: photo.s3Url || photo.url || null,
    angle: photo.angle || photo.type || null,
    contentType: photo.contentType || null,
    sizeBytes: Number.isFinite(Number(photo.sizeBytes))
      ? Number(photo.sizeBytes)
      : null,
    uploadedAt: photo.uploadedAt || null,
    iaValidated: photo.iaValidated === true,
    raw: photo,
  };
}

/**
 * Imagen de prenda: FRONTAL con s3Url, si no la primera con URL.
 * TODO: remove snapshot fallback for guarantee image once Backend
 * adds guaranteeImageUrl or photo URL in GET /digital-loan/status.
 */
export function pickGuaranteeImageUrl(photos = []) {
  const list = Array.isArray(photos) ? photos : [];
  const withUrl = list
    .map((photo) => mapPhotoUploaded(photo))
    .filter((photo) => photo.s3Url);
  const frontal = withUrl.find((photo) => photo.angle === 'FRONTAL');
  return frontal?.s3Url || withUrl[0]?.s3Url || null;
}

export function mapGuaranteeResponse(response = {}) {
  const photos = Array.isArray(response.photos)
    ? response.photos.map(mapPhotoUploaded)
    : [];
  const observaciones = Array.isArray(response.photoMatchObservaciones)
    ? response.photoMatchObservaciones
    : [];

  return {
    applicationId: response.applicationId || null,
    photos,
    currentPhase: response.currentPhase || null,
    currentStep: response.currentStep || null,
    photoMatchOutcome: response.photoMatchOutcome || null,
    photoMatchConfidence: Number.isFinite(Number(response.photoMatchConfidence))
      ? Number(response.photoMatchConfidence)
      : null,
    photoMatchObservaciones: observaciones,
    guaranteeImageUrl: pickGuaranteeImageUrl(photos),
    raw: response,
  };
}

function mapScoringFields(response = {}) {
  return {
    applicationId: response.applicationId || null,
    iaMatchPassed: response.iaMatchPassed,
    amountSource: response.amountSource || null,
    finalMarketValue: response.finalMarketValue ?? null,
    appraisalAmount: response.appraisalAmount ?? null,
    suggestedLoanAmount: response.suggestedLoanAmount ?? null,
    cappedByMaxAmount: response.cappedByMaxAmount === true,
    maxLoanAmount: response.maxLoanAmount ?? null,
    riskLevel: response.riskLevel || null,
    riskLevelLabel: response.riskLevelLabel || null,
    interestRate: response.interestRate ?? null,
    express: response.express === true,
    bcPoints: response.bcPoints ?? null,
    prendaPoints: response.prendaPoints ?? null,
    arraigoPoints: response.arraigoPoints ?? null,
    totalPoints: response.totalPoints ?? null,
    phase2Completed: response.phase2Completed === true,
    currentPhase: response.currentPhase || null,
    currentStep: response.currentStep || null,
    status: response.status || null,
    rejectionReason: response.rejectionReason || null,
    errorCode: response.errorCode || null,
    completedAt: response.completedAt || null,
  };
}

export function mapAmountAndScoringResponse(response = {}, context = {}) {
  // Swagger: IA mismatch va por iaMatchPassed; KNOCK_OUT / VALUE_INSUFFICIENT
  // van en rejectionReason o errorCode. IA_MISMATCH se conserva por compat.
  const rejectionReason = response.rejectionReason || response.errorCode;
  const scoring = mapScoringFields(response);

  if (response.iaMatchPassed === false || rejectionReason === 'IA_MISMATCH') {
    return {
      status: PHASE2_MOCK_SCENARIOS.VALIDATION_FAILED,
      valid: false,
      scoring,
      raw: response,
    };
  }

  if (rejectionReason === 'VALUE_INSUFFICIENT') {
    return {
      status: PHASE2_MOCK_SCENARIOS.INSUFFICIENT_VALUE,
      valid: false,
      scoring,
      raw: response,
    };
  }

  if (rejectionReason === 'KNOCK_OUT' || response.phase2Completed === false) {
    return {
      status: PHASE2_MOCK_SCENARIOS.NOT_PREAPPROVED,
      summary: {
        amount: context.amount,
        term: context.term,
        paymentFrequency: PAYMENT_FREQUENCY_LABEL[context.paymentFrequency]
          || 'Mensual',
        paymentDay: '14 de cada mes',
        rate: response.interestRate ?? context.rate,
      },
      scoring,
      raw: response,
    };
  }

  const preOffer = buildMockPreOffer({
    amount: response.suggestedLoanAmount ?? context.amount,
    term: context.term,
    paymentFrequencyLabel: PAYMENT_FREQUENCY_LABEL[context.paymentFrequency]
      || 'Mensual',
    guaranteeName: context.guaranteeName || 'Prenda',
    rate: response.interestRate ?? context.rate,
  });

  return {
    status: PHASE2_MOCK_SCENARIOS.PREOFFER,
    preOffer: {
      ...preOffer,
      appraisalAmount: scoring.appraisalAmount,
      suggestedLoanAmount: scoring.suggestedLoanAmount,
      cappedByMaxAmount: scoring.cappedByMaxAmount,
      maxLoanAmount: scoring.maxLoanAmount,
      riskLevel: scoring.riskLevel,
      riskLevelLabel: scoring.riskLevelLabel,
      interestRate: scoring.interestRate,
      express: scoring.express,
    },
    scoring,
    raw: response,
  };
}

export { ANGLE_ORDER, CONSERVATION_STATE_MAP };
